import { useState, useEffect } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Box,
} from '@mui/material';
import { chain } from 'lodash';
import { collection, query, where, orderBy, getDocs } from 'firebase/firestore';
import theme from 'theme';
import { apiClient } from '../../../api-client/index.js';
import { Portlet, PortletHeader, PortletLabel, PortletContent } from '../../../components/Portlets';
import { Status } from '../../../components/Status';
import { Cell } from '../../../components/Cell';
import { STATUS_COLORS } from '../../../constants/requestStatus';
import { formatDate, snapToArray } from '../../../utils';
import { useFirestore } from '../../../utils/firebase';
import { useAuth } from '../../../auth';
import { RequestAction } from './request-action';
import { Controls } from './controls';

const TeamHardwareRequestsTable = () => {
  const { user: authenticatedUser, isOfficeManager, getToken } = useAuth();
  const [isLoading, setLoading] = useState(false);
  const [requests, setRequests] = useState([]);
  const [statusFilter, setStatusFilter] = useState(['pending', 'approved']);
  const [nameFilter, setNameFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const database = useFirestore();

  const filterRequests = requests => {
    if (!requests) {
      return null;
    }

    const filterByName = ({ requestedBy: { firstName, lastName } }) => {
      const fields = [firstName, lastName, `${firstName} ${lastName}`];

      return fields.some(string => string.toLowerCase().startsWith(nameFilter.toLowerCase()));
    };

    return chain(requests).filter(filterByName).value();
  };

  const formatRequestedBy = ({ firstName, lastName } = {}) =>
    firstName && lastName ? (
      <div>
        {firstName} {lastName}
      </div>
    ) : (
      <div>{firstName || lastName}</div>
    );

  const updateRequests = input => {
    setRequests(prev =>
      prev
        .map(request => (request.id === input.requestId ? { ...request, ...input } : request))
        .filter(request => statusFilter.length === 0 || statusFilter.includes(request.status)),
    );
  };

  const requestHardwareStatusUpdate = async (requestId, status, note) => {
    const result = await apiClient('POST', `/requests/hardware/${requestId}/status`, getToken, {
      status,
      ...(note !== undefined && { note }),
    });

    if (result?.status !== 'OK') {
      throw new Error(result?.message || 'Request failed');
    }
  };

  const onRequestAction = async ({ requestId, status }) => {
    await requestHardwareStatusUpdate(requestId, status);
    updateRequests({ requestId, status });
    return true;
  };

  const onDenyRequestAction = async ({ requestId, status, note }) => {
    await requestHardwareStatusUpdate(requestId, status, note);
    updateRequests({ requestId, status });
    return true;
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredRequests = filterRequests(requests);

  const hasActionableRequests = filteredRequests.some(request =>
    isOfficeManager
      ? request.status === 'approved'
      : request.status === 'pending' || request.status === 'approved',
  );

  useEffect(() => {
    setLoading(true);

    let queryRequests = query(
      collection(database, 'hardware_requests'),
      where('userTenant', '==', authenticatedUser.tenant.id),
      orderBy('requestedAt', 'desc'),
    );

    if (statusFilter.length !== 0) {
      queryRequests = query(
        collection(database, 'hardware_requests'),
        where('userTenant', '==', authenticatedUser.tenant.id),
        where('status', 'in', statusFilter),
        orderBy('requestedAt', 'desc'),
      );
    }

    getDocs(queryRequests)
      .then(snapshot => {
        setRequests(snapToArray(snapshot));
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [authenticatedUser.tenant.id, database, statusFilter]);

  return (
    <Portlet
      sx={{ background: 'transparent', border: 'none', boxShadow: 'none' }}
      outlined={1}
      squared={false}
    >
      <PortletHeader
        sx={{
          '@media (max-width:480px)': {
            display: 'table',
            paddingBottom: '1.5rem',
          },
        }}
        noDivider
      >
        <PortletLabel
          subtitle={`${filteredRequests.length} in total`}
          title="Latest Requests"
          sx={{
            '@media (max-width:480px)': {
              '& h5': {
                fontSize: '1.3rem',
              },
              '& h6': {
                marginLeft: '0.5rem',
                fontSize: '0.7rem',
                width: '3rem',
              },
            },
          }}
        />

        <Controls
          statusFilter={statusFilter}
          nameFilter={nameFilter}
          setNameFilter={setNameFilter}
          setStatusFilter={setStatusFilter}
          setPage={setPage}
        />
      </PortletHeader>

      <PerfectScrollbar>
        <PortletContent isLoading={isLoading} noPadding>
          {filteredRequests.length ? (
            <>
              <Table
                sx={{
                  '& th': {
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>Request by</TableCell>
                    <TableCell>Requested at</TableCell>
                    <TableCell>Title</TableCell>
                    <TableCell>Description</TableCell>
                    {(['pending', 'all', 'approved'].some(item => statusFilter.includes(item)) ||
                      statusFilter.length === 0) &&
                      hasActionableRequests && <TableCell>Actions</TableCell>}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredRequests
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map(
                      ({
                        id: requestId,
                        userId,
                        requestedBy = {},
                        description,
                        title,
                        requestedAt,
                        status = 'pending',
                      }) => (
                        <TableRow
                          sx={{ cursor: 'pointer' }}
                          hover
                          key={requestId}
                          data-cy={description}
                        >
                          <Cell>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Status
                                sx={{ marginRight: theme.spacing(1) }}
                                color={STATUS_COLORS[status]}
                                size="sm"
                              />
                              {status}
                            </Box>
                          </Cell>
                          <Cell>{formatRequestedBy(requestedBy)}</Cell>
                          <Cell>{formatDate(requestedAt, 'MMMM dd, yyyy')}</Cell>
                          <Cell>{title}</Cell>
                          <Cell>{description}</Cell>
                          {hasActionableRequests && (
                            <Cell width={150}>
                              {status === 'pending' || status === 'approved' ? (
                                <RequestAction
                                  userId={userId}
                                  requestId={requestId}
                                  status={status}
                                  requestedBy={requestedBy}
                                  description={description}
                                  title={title}
                                  onRequestAction={onRequestAction}
                                  onDenyRequestAction={onDenyRequestAction}
                                />
                              ) : (
                                <div />
                              )}
                            </Cell>
                          )}
                        </TableRow>
                      ),
                    )}
                </TableBody>
              </Table>

              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                onRowsPerPageChange={handleChangeRowsPerPage}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                component="div"
                count={filteredRequests.length}
                page={page}
              />
            </>
          ) : (
            <Box sx={{ padding: 24, paddingTop: 0, fontWeight: 300, color: '#777' }}>
              No requests found
            </Box>
          )}
        </PortletContent>
      </PerfectScrollbar>
    </Portlet>
  );
};

export default TeamHardwareRequestsTable;
